import { useLocalStorage } from './useLocalStorage';

const COOKIE_CONSENT_KEY = 'probizpay_cookie_consent';
const CURRENT_POLICY_VERSION = '2026-01-12';

interface CookieConsentData {
  consentGiven: boolean;
  consentTimestamp: string;
  policyVersion: string;
  categories: {
    essential: true;
    functional: boolean;
    analytics: boolean;
  };
}

const DEFAULT_CONSENT: CookieConsentData = {
  consentGiven: false,
  consentTimestamp: '',
  policyVersion: '',
  categories: {
    essential: true,
    functional: false,
    analytics: false,
  },
};

export function useCookieConsent() {
  const [consent, setConsent] = useLocalStorage<CookieConsentData>(
    COOKIE_CONSENT_KEY,
    DEFAULT_CONSENT
  );

  const shouldShowBanner =
    !consent.consentGiven || consent.policyVersion !== CURRENT_POLICY_VERSION;

  const acceptAll = () => {
    setConsent({
      consentGiven: true,
      consentTimestamp: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
      categories: { essential: true, functional: true, analytics: true },
    });
  };

  const rejectNonEssential = () => {
    setConsent({
      consentGiven: true,
      consentTimestamp: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
      categories: { essential: true, functional: false, analytics: false },
    });
  };

  const savePreferences = (functional: boolean, analytics: boolean) => {
    setConsent({
      consentGiven: true,
      consentTimestamp: new Date().toISOString(),
      policyVersion: CURRENT_POLICY_VERSION,
      categories: { essential: true, functional, analytics },
    });
  };

  const isCategoryAllowed = (category: 'essential' | 'functional' | 'analytics') => {
    if (category === 'essential') return true;
    return consent.consentGiven && consent.categories[category];
  };

  return {
    consent,
    shouldShowBanner,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    isCategoryAllowed,
  };
}
